import { cn } from '@spiaggeit/spit-ui'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Service } from '@/models/service'

import { SERVICE_LABEL_LIMIT } from '@/utils/constants'
import { Minus } from '../../assets/icons/Minus'
import { Plus } from '../../assets/icons/Plus'
import { priceFormatter } from '../../utils/price'
import { IconButton } from '../IconButton'
import { Tooltip } from '../Tooltip/Tooltip'

interface Props {
  areLimitsReactive?: boolean
  available: {
    global?: number
    group?: number
    ticket: number
  }
  hasLimit: boolean
  icon: string
  name: string
  minLabel?: string
  maxLabel?: string
  minimumQuantity: number
  price?: number
  initialQuantity?: number
  showSoldOutLabel?: boolean
  ticket?: Service
  onAdd: VoidFunction
  onRemove: VoidFunction
  onAddDisabled?: VoidFunction
}

export const ItemQuantityController = (props: Props) => {
  const { t } = useTranslation()
  const [quantity, setQuantity] = useState(props.initialQuantity || 0)

  const global = props.available?.global ?? Infinity
  const group = props.available?.group ?? Infinity
  const ticket = props.available?.ticket ?? Infinity

  const max = props.hasLimit ? Math.min(global, group, ticket) : Infinity
  const availableQuantity = props.hasLimit
    ? Math.min(global, group, ticket - quantity)
    : Infinity

  const isSoldOut = props.hasLimit && props?.available.ticket === 0

  useEffect(() => {
    if (props.initialQuantity) {
      setQuantity(props.initialQuantity)
    } else if (props.available.ticket < quantity) {
      setQuantity(props.available.ticket)
    }
  }, [props.initialQuantity, props.available.ticket])

  useEffect(() => {
    if (
      props.areLimitsReactive &&
      quantity >= 0 &&
      quantity < props.minimumQuantity
    ) {
      setQuantity(quantity + 1)
      props.onAdd()
    }
  }, [props.minimumQuantity, props.areLimitsReactive])

  useEffect(() => {
    if (
      props.areLimitsReactive &&
      quantity > 0 &&
      quantity > props.available.ticket
    ) {
      setQuantity(quantity - 1)
      props.onRemove()
    }
  }, [max, quantity, props.areLimitsReactive])

  const handleRemove = () => {
    if (quantity > props.minimumQuantity) {
      setQuantity(quantity - 1)
      props.onRemove()
    }
  }

  const handleAdd = () => {
    if (availableQuantity > 0) {
      setQuantity(quantity + 1)
      props.onAdd()
    }
  }

  const isAddDisabled = availableQuantity < 1
  const isRemoveDisabled = quantity === 0 || quantity <= props.minimumQuantity

  const minLabel =
    props.minLabel || t('listItem.min', { value: props.minimumQuantity })

  const maxLabel =
    availableQuantity < SERVICE_LABEL_LIMIT
      ? props.maxLabel || t('listItem.max', { value: props.available.ticket })
      : ''

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 first:rounded-t last:rounded-b',
        {
          'cursor-not-allowed opacity-50': isSoldOut,
        }
      )}
    >
      <div className="flex flex-auto items-center gap-3">
        <div className="flex-initial shrink-0">
          <img
            alt={props.name}
            className="size-8"
            src={`/icons/${props.icon}`}
          />
        </div>

        <div className="flex-auto">
          <Tooltip
            className="font-medium"
            content={props.name}
            maxLines={3}
            overflowStyle="underline"
          />

          <p className="text-sm leading-5">
            {props.price && props.price > 0 && (
              <span>{priceFormatter.format(props.price)} </span>
            )}

            {props.showSoldOutLabel ? (
              <span className="text-secondary">{t('listItem.soldOut')}</span>
            ) : null}
            <span className="text-secondary">
              {props.minimumQuantity > 0 ? <> {minLabel}</> : null}
              {props.minimumQuantity > 0 && props.hasLimit ? ' · ' : null}
              {props.hasLimit ? <>{maxLabel}</> : null}
            </span>
          </p>
        </div>
      </div>

      <div className="flex-0 relative flex items-center">
        <IconButton
          ariaLabel={t('itemQuantityController.remove')}
          icon={Minus}
          isDisabled={isRemoveDisabled}
          onClick={handleRemove}
        />

        <div className="flex size-10 items-center justify-center text-sm font-bold">
          {quantity}
        </div>

        <IconButton
          ariaLabel={t('itemQuantityController.add')}
          icon={Plus}
          isDisabled={isAddDisabled}
          onClick={handleAdd}
        />

        {props.onAddDisabled && isAddDisabled && (
          <button
            className="absolute right-0 top-0 size-10"
            onClick={props.onAddDisabled}
          />
        )}
      </div>
    </div>
  )
}
